import { Aircraft } from '../types/intellijetnet'
import { AircraftOverride, OffMarket, AircraftChangeInfo } from '../types/database'
import { AircraftAuditService } from '../services/intellijetnet/aircraft-audit.service'
import { AircraftAcknowledgmentsService } from '../services/aircraft-acknowledgments.service'
import { getEffectiveAircraftData } from './overrides'

/**
 * Check if an aircraft is off-market (manually added)
 */
export function isOffMarketAircraft(aircraftId: number, offMarketAircraft: OffMarket[]): boolean {
  return offMarketAircraft.some(om => om.aircraft_id === aircraftId)
}

/**
 * Check if aircraft status indicates "Under Contract"
 */
export function isUnderContract(aircraft: Aircraft, override?: AircraftOverride | null): boolean {
  const effectiveAircraft = getEffectiveAircraftData(aircraft, override)
  const status = effectiveAircraft.status?.toLowerCase() || ''
  
  // Check for common "under contract" status variations
  const underContractKeywords = [
    'under contract',
    'pending',
    'sold pending',
    'contract pending',
    'in escrow',
    'reserved'
  ]
  
  return underContractKeywords.some(keyword => status.includes(keyword))
}

/**
 * Check if aircraft is newly listed (within last 30 days)
 */
export function isNewlyListed(aircraft: Aircraft, override?: AircraftOverride | null): boolean {
  const effectiveAircraft = getEffectiveAircraftData(aircraft, override)
  const dateListedStr = effectiveAircraft.datelisted
  
  if (!dateListedStr) return false
  
  const dateListed = new Date(dateListedStr)
  const thirtyDaysAgo = new Date()
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
  
  return dateListed >= thirtyDaysAgo
}

/**
 * Detect changes for a single aircraft
 */
export async function detectAircraftChanges(
  aircraft: Aircraft,
  override?: AircraftOverride | null,
  offMarketAircraft: OffMarket[] = []
): Promise<AircraftChangeInfo> {
  const aircraftId = aircraft.aircraft_id
  
  // Initialize change info
  const changeInfo: AircraftChangeInfo = {
    aircraft_id: aircraftId,
    has_price_change: false,
    is_new_to_market: false,
    is_under_contract: false,
    last_acknowledged: {}
  }

  // Skip change detection for off-market aircraft
  if (isOffMarketAircraft(aircraftId, offMarketAircraft)) {
    return changeInfo
  }

  // Check under contract status
  changeInfo.is_under_contract = isUnderContract(aircraft, override)

  // Check if newly listed
  changeInfo.is_new_to_market = isNewlyListed(aircraft, override)
  if (changeInfo.is_new_to_market) {
    const effectiveAircraft = getEffectiveAircraftData(aircraft, override)
    changeInfo.new_listing_date = effectiveAircraft.datelisted
  }

  try {
    // Get acknowledgment status
    const acknowledgmentStatus = await AircraftAcknowledgmentsService.getBulkAcknowledmentStatus([aircraftId])
    const status = acknowledgmentStatus.get(aircraftId)
    if (status) {
      changeInfo.last_acknowledged = {
        price_change: status.price_change,
        new_to_market: status.new_to_market
      }
    }

    // Check for price changes since last acknowledgment
    // Use price_change ack as baseline, or fall back to new_to_market ack
    // (acknowledging a new listing means the user has seen the aircraft's current state,
    // so only price changes after that point should trigger highlighting)
    const lastPriceAck = status?.price_change
    const newListingAckDate = status?.new_to_market
    const priceBaselineDate = lastPriceAck || newListingAckDate
    if (priceBaselineDate) {
      const hasPriceChanges = await AircraftAuditService.hasPriceChangesSince(aircraftId, priceBaselineDate)
      changeInfo.has_price_change = hasPriceChanges
    } else {
      // No previous acknowledgment of any kind, check for any recent price changes (last 30 days)
      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
      const hasPriceChanges = await AircraftAuditService.hasPriceChangesSince(aircraftId, thirtyDaysAgo.toISOString())
      changeInfo.has_price_change = hasPriceChanges
    }

    // Get price change details if there are changes
    if (changeInfo.has_price_change) {
      const latestPriceChange = await AircraftAuditService.getLatestPriceChange(aircraftId)
      if (latestPriceChange && latestPriceChange.old_values && latestPriceChange.new_values) {
        changeInfo.price_change_details = {
          previous_asking: latestPriceChange.old_values.asking,
          new_asking: latestPriceChange.new_values.asking,
          previous_take: latestPriceChange.old_values.take_price,
          new_take: latestPriceChange.new_values.take_price
        }
      }
    }

    // Override new_to_market flag if already acknowledged
    if (changeInfo.is_new_to_market) {
      const newListingAck = status?.new_to_market
      if (newListingAck && changeInfo.new_listing_date) {
        const listingDate = new Date(changeInfo.new_listing_date)
        const ackDate = new Date(newListingAck)
        if (ackDate >= listingDate) {
          changeInfo.is_new_to_market = false
        }
      }
    }

    // New listing takes precedence over price change — suppress price change if new listing
    if (changeInfo.is_new_to_market) {
      changeInfo.has_price_change = false
      changeInfo.price_change_details = undefined
    }

  } catch (error) {
    // Error detecting changes for aircraft
  }

  return changeInfo
}

/**
 * Detect changes for multiple aircraft in bulk
 */
export async function detectBulkAircraftChanges(
  aircraft: Aircraft[],
  overrides: Map<number, AircraftOverride>,
  offMarketAircraft: OffMarket[] = []
): Promise<Map<number, AircraftChangeInfo>> {
  const changeMap = new Map<number, AircraftChangeInfo>()
  
  // Filter out off-market aircraft
  const apiAircraft = aircraft.filter(a => !isOffMarketAircraft(a.aircraft_id, offMarketAircraft))
  
  if (apiAircraft.length === 0) {
    return changeMap
  }

  const aircraftIds = apiAircraft.map(a => a.aircraft_id)

  try {
    // Get bulk acknowledgment status
    const acknowledgmentStatus = await AircraftAcknowledgmentsService.getBulkAcknowledmentStatus(aircraftIds)

    // Get bulk price changes for aircraft that need checking
    const aircraftToCheck: { id: number; sinceDate: string }[] = []
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

    for (const aircraft of apiAircraft) {
      const status = acknowledgmentStatus.get(aircraft.aircraft_id)
      const sinceDate = status?.price_change || thirtyDaysAgo.toISOString()
      aircraftToCheck.push({ id: aircraft.aircraft_id, sinceDate })
    }

    // Get price changes for all aircraft
    const bulkPriceChanges = await AircraftAuditService.getBulkPriceChanges(
      aircraftIds,
      thirtyDaysAgo.toISOString()
    )

    // Process each aircraft
    for (const aircraftItem of apiAircraft) {
      const aircraftId = aircraftItem.aircraft_id
      const override = overrides.get(aircraftId)
      const status = acknowledgmentStatus.get(aircraftId)

      const changeInfo: AircraftChangeInfo = {
        aircraft_id: aircraftId,
        has_price_change: false,
        is_new_to_market: false,
        is_under_contract: false,
        last_acknowledged: {
          price_change: status?.price_change,
          new_to_market: status?.new_to_market
        }
      }

      // Check under contract status
      changeInfo.is_under_contract = isUnderContract(aircraftItem, override)

      // Check if newly listed
      changeInfo.is_new_to_market = isNewlyListed(aircraftItem, override)
      if (changeInfo.is_new_to_market) {
        const effectiveAircraft = getEffectiveAircraftData(aircraftItem, override)
        changeInfo.new_listing_date = effectiveAircraft.datelisted

        // Check if already acknowledged
        const newListingAck = status?.new_to_market
        if (newListingAck && changeInfo.new_listing_date) {
          const listingDate = new Date(changeInfo.new_listing_date)
          const ackDate = new Date(newListingAck)
          if (ackDate >= listingDate) {
            changeInfo.is_new_to_market = false
          }
        }
      }

      // Check for price changes (only if not a new listing — new listing takes precedence)
      if (!changeInfo.is_new_to_market) {
        const priceChanges = bulkPriceChanges.get(aircraftId) || []
        if (priceChanges.length > 0) {
          // Use price_change ack as baseline, or fall back to new_to_market ack
          // (acknowledging a new listing means the user has seen the aircraft's current state,
          // so only price changes after that point should trigger highlighting)
          const lastPriceAck = status?.price_change
          const newListingAck = status?.new_to_market
          const priceBaselineDate = lastPriceAck || newListingAck
          if (priceBaselineDate) {
            // Check if there are changes since last acknowledgment
            const changesAfterAck = priceChanges.filter(change =>
              new Date(change.changed_at) > new Date(priceBaselineDate)
            )
            changeInfo.has_price_change = changesAfterAck.length > 0
          } else {
            // No previous acknowledgment of any kind, any recent change counts
            changeInfo.has_price_change = true
          }

          // Get price change details from latest change
          if (changeInfo.has_price_change && priceChanges[0]) {
            const latestChange = priceChanges[0]
            if (latestChange.old_values && latestChange.new_values) {
              changeInfo.price_change_details = {
                previous_asking: latestChange.old_values.asking,
                new_asking: latestChange.new_values.asking,
                previous_take: latestChange.old_values.take_price,
                new_take: latestChange.new_values.take_price
              }
            }
          }
        }
      }

      changeMap.set(aircraftId, changeInfo)
    }

  } catch (error) {
    // Error detecting bulk changes
  }

  return changeMap
}

/**
 * Get highlight class for aircraft based on change status
 */
export function getAircraftHighlightClass(changeInfo: AircraftChangeInfo): string {
  // Priority order: Under Contract (yellow) > New to Market (purple) > Price Change (blue)
  if (changeInfo.is_under_contract) {
    return 'bg-yellow-100'
  }
  if (changeInfo.is_new_to_market) {
    return 'bg-purple-100'
  }
  if (changeInfo.has_price_change) {
    return 'bg-blue-100'
  }
  return ''
}

/**
 * Check if aircraft should show acknowledgment button
 */
export function shouldShowAcknowledgmentButton(changeInfo: AircraftChangeInfo): boolean {
  // Show button if there are price changes or new listing (but not just under contract)
  return changeInfo.has_price_change || changeInfo.is_new_to_market
}
